import { Mesh, Vector3 } from "three";
import { useFrame } from "@react-three/fiber";
import { useMemo } from "react";
import { MeshFeatureContextHook } from "../MeshFeatureContext";
import { getUserDataProperties } from "../FeatureUtils";

export const WAVE_HEIGHT = 0.006;

export type Wave = {
  y: number;
  x: number;
  z: number;
  ang: number;
  amp: number;
  speed: number;
};

const getMeshVertices = (mesh: Mesh) => {
  const posAtt = mesh.geometry.getAttribute("position");
  const l = posAtt.count;

  return new Array(l)
    .fill(0)
    .map((_, i) => mesh.getVertexPosition(i, new Vector3()));
};

export const useWater: MeshFeatureContextHook = (mesh: Mesh, userData = {}) => {
  const objectUserData = getUserDataProperties(userData);
  const { Water: unprocessedValue = undefined } = objectUserData;
  const water = !!unprocessedValue;
  const waves: Wave[] = useMemo(() => {
    if (water && mesh?.isMesh) {
      return getMeshVertices(mesh).map((_, i) => {
        const v = mesh.getVertexPosition(i, new Vector3());

        return {
          y: v.y,
          x: v.x,
          z: v.z,
          ang: Math.random() * Math.PI * 2,
          amp: WAVE_HEIGHT,
          speed: 0.016 + Math.random() * 0.024,
        };
      });
    } else {
      return [];
    }
  }, [water, mesh]);

  useFrame(() => {
    if (water) {
      const verts = getMeshVertices(mesh);
      const pos = mesh.geometry.getAttribute("position");
      const l = verts.length;

      for (let i = 0; i < l; i++) {
        const v = verts[i];
        const vprops = waves[i];

        v.x = vprops.x + Math.cos(vprops.ang) * vprops.amp;
        v.y = vprops.y + Math.sin(vprops.ang) * vprops.amp;
        vprops.ang += vprops.speed;

        pos.setXYZ(i, v.x, v.y, v.z);
        pos.needsUpdate = true;
      }
    }
  });
};
